import { Box, Chip, Divider, TextField, Typography } from "@mui/material";
import { message } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../axios/axios-instance";
import ActionButton from "../../components/common/buttons/ActionButton";
import CancelButton from "../../components/common/buttons/CancelButton";
import Card from "../../components/common/Card";
import { pathAPI } from "../../utils/pathApi";
import { useBreadcrumbLabel } from "../../components/breadcrumb/BreadcrumbContext";

const terminalStatuses = ["APPROVED", "REJECTED", "CANCELLED"];

const valueText = (value: any): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const payloadEntries = (payload: any): [string, any][] => {
  if (!payload || typeof payload !== "object") {
    return [];
  }
  return Object.entries(payload);
};

const PayloadPanel = ({ title, payload }: { title: string; payload: any }) => {
  const { t } = useTranslation();
  return (
  <Box flex={1} minWidth={320}>
    <Typography fontWeight="bold" mb={1}>
      {title}
    </Typography>
    <Box border="1px solid var(--app-border)" borderRadius={2} overflow="hidden">
      {payloadEntries(payload).map(([key, value]) => (
        <Box
          key={key}
          display="grid"
          gridTemplateColumns="160px 1fr"
          borderBottom="1px solid var(--app-border-light)"
        >
          <Box bgcolor="action.hover" p={1.5} fontWeight="bold">
            {key}
          </Box>
          <Box p={1.5} whiteSpace="pre-wrap" sx={{ wordBreak: "break-word" }}>
            {Array.isArray(value) && key === "gallery" ? (
              <Box display="flex" gap={1} flexWrap="wrap">
                {value.map((url: string, index: number) => (
                  <Box key={`${url}-${index}`} display="flex" flexDirection="column" gap={0.5}>
                    <img
                      src={url}
                      alt={`gallery-${index}`}
                      style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 6 }}
                    />
                    <Typography fontSize={11} maxWidth={96} noWrap>
                      {url}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : key === "featured_photo" && value ? (
              <Box display="flex" alignItems="center" gap={1.5}>
                <img
                  src={value}
                  alt="featured"
                  style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 6 }}
                />
                <Typography fontSize={12} sx={{ wordBreak: "break-all" }}>
                  {valueText(value)}
                </Typography>
              </Box>
            ) : (
              valueText(value)
            )}
          </Box>
        </Box>
      ))}
      {payloadEntries(payload).length === 0 && <Box p={2}>{t("outlet_change_request.detail.no_payload")}</Box>}
    </Box>
  </Box>
  );
};

const OutletChangeRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [request, setRequest] = useState<any>(null);
  useBreadcrumbLabel(request?.outlet_name);
  const [loading, setLoading] = useState(false);
  const [adminNote, setAdminNote] = useState("");

  const fetchRequest = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`${pathAPI.outletChangeRequests}/${id}`);
      setRequest(response.data?.data || response.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const review = async (action: "approve" | "reject") => {
    if (!id) return;
    if (action === "reject" && !adminNote.trim()) {
      message.error(t("outlet_change_request.detail.note_required"));
      return;
    }
    if (!window.confirm(t(`outlet_change_request.detail.confirm_${action}`))) {
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.patch(
        `${pathAPI.outletChangeRequests}/${id}/${action}`,
        { admin_note: adminNote.trim() || undefined }
      );
      setRequest(response.data?.data || response.data);
      message.success(t(`outlet_change_request.detail.${action}d_ok`));
    } finally {
      setLoading(false);
    }
  };

  if (!request) {
    return (
      <Card>
        <Typography>{loading ? t("outlet_change_request.detail.loading") : t("outlet_change_request.detail.not_found")}</Typography>
      </Card>
    );
  }

  const isTerminal = terminalStatuses.includes(request.status);

  return (
    <Card>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {t("outlet_change_request.detail.title", { id: request.id })}
          </Typography>
          <Typography color="text.secondary">
            {request.outlet_name || "-"} · {request.request_type || "-"}
          </Typography>
        </Box>
        <CancelButton onClick={() => navigate("/outlet-change-requests")}>{t("outlet_change_request.detail.back")}</CancelButton>
      </Box>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Chip label={t(`outlet_change_request.status.${request.status}`, { defaultValue: request.status })} color={request.status === "APPROVED" ? "success" : "default"} />
        {request.support_ticket_id && <Chip label={t("outlet_change_request.detail.ticket", { id: request.support_ticket_id })} />}
        {request.created_date && (
          <Chip label={t("outlet_change_request.detail.created", { date: moment(request.created_date).format("YYYY/MM/DD HH:mm:ss") })} />
        )}
      </Box>

      <Typography fontWeight="bold">{t("outlet_change_request.detail.owner_reason")}</Typography>
      <Typography mb={2} whiteSpace="pre-wrap">
        {request.reason || "-"}
      </Typography>

      <Divider sx={{ my: 2 }} />

      <Box display="flex" gap={2} flexWrap="wrap">
        <PayloadPanel title={t("outlet_change_request.detail.current_data")} payload={request.current_payload} />
        <PayloadPanel title={t("outlet_change_request.detail.requested_data")} payload={request.requested_payload} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <TextField
        fullWidth
        label={t("outlet_change_request.detail.admin_note")}
        minRows={3}
        multiline
        value={adminNote || request.admin_note || ""}
        disabled={isTerminal}
        onChange={(event) => setAdminNote(event.target.value)}
      />

      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        <CancelButton disabled={loading || isTerminal} onClick={() => review("reject")}>
          {t("outlet_change_request.detail.reject")}
        </CancelButton>
        <ActionButton disabled={loading || isTerminal} onClick={() => review("approve")}>
          {t("outlet_change_request.detail.approve")}
        </ActionButton>
      </Box>
    </Card>
  );
};

export default OutletChangeRequestDetail;
